import {
  DispatchDifficultyLevel,
  DispatchDuration,
  DispatchType,
  EquipmentRarity,
  MateDispatchStatusRequest,
  MateInteractionStatus,
} from "@/enums/mission";
import { deleteOne, get, post, put } from "@/modules/request";
import {
  PageRequest,
  PageResponse,
  Response,
  WriteResponse,
} from "@/types/api-types";
import {
  DispatchMission,
  MateDispatch,
  MateDispatchMember,
  MateEquipment,
  MateJournal,
} from "@/types/entity";

export const ApiGetDispatchMission = (
  uuid: string
): Promise<WriteResponse<DispatchMission>> => get(`/DispatchMission/${uuid}`);

export const ApiGetDispatchMissionPage = (
  request: PageRequest<null> = {
    pagination: { page: 1, perPage: 999 },
  }
): Promise<PageResponse<DispatchMission>> =>
  get("/DispatchMissionPage", request);

export type ApiMateDispatchParams = {
  type: DispatchType;
  mateId?: number;
  duration: DispatchDuration;
  missionId?: number;
  locationId?: number;
  objective?: string;
  objectiveSignature?: string;
  difficultyLevel?: DispatchDifficultyLevel;
  difficultyLevelReason?: string;
  isRankingEnabled?: boolean;
  memberzz?: MateDispatchMember[];
  carriedEquipmentIdzz?: number[];
  carriedJournalIdzz?: number[];
  carriedMailIdzz?: number[];
  carriedDispatchIdzz?: number[];
  carriedMemoryIdzz?: number[];
  dispatchTemplateId?: number;
};

export const ApiCreateMateDispatch = (
  request: ApiMateDispatchParams
): Promise<WriteResponse<MateDispatch>> => post("/MateDispatch", request);

export const ApiUpdateMateDispatch = (
  id: number,
  request: ApiMateDispatchParams
): Promise<WriteResponse<MateDispatch>> => put(`/MateDispatch/${id}`, request);

export const ApiGetMateDispatchMatePage = (
  request: PageRequest<{ interactionStatus?: MateInteractionStatus }>
): Promise<PageResponse<MateDispatch>> =>
  get("/MateDispatch/ReadMatePage", request);

export const ApiGetMateDispatch = (
  uuid: string,
  mateId?: number
): Promise<WriteResponse<MateDispatch>> =>
  get(`/MateDispatch/${uuid}${mateId ? `?mateId=${mateId}` : ""}`);

export const ApiGetMateDispatchPage = (
  request: PageRequest<{
    mateId?: number;
    status?: MateDispatchStatusRequest;
    type?: DispatchType;
    coinRankId?: number;
  }> = {
    pagination: { page: 1, perPage: 999 },
  }
): Promise<PageResponse<MateDispatch>> => get("/MateDispatchPage", request);

export const ApiCancelInvitation = (
  id: number
): Promise<WriteResponse<MateDispatch>> => post(`/MateDispatchCancel/${id}`);

export const ApiRetryDispatchMission = (
  id: number,
  data?: {
    memberzz?: MateDispatchMember[];
  }
): Promise<WriteResponse<MateDispatch>> =>
  post(`/MateDispatchRetry/${id}`, data);

// MateDispatch/GenerateObjective
export const ApiPostGenerateObjective = (params: {
  mateId: number;
  missionId: number;
  locationId: number;

  memberIdzz?: number[];
  objective?: string;
  difficultyLevel?: DispatchDifficultyLevel;
  carriedJournalIdzz?: number[];
  carriedMailIdzz?: number[];
  carriedDispatchIdzz?: number[];
}): Promise<
  Response<{
    difficultyLevel: DispatchDifficultyLevel;
    goalContent: string;
    reason: string;
    signature: string;
  }>
> => post(`/MateDispatch/GenerateObjective`, params);

export const ApiSettleMateDispatch = (
  id: number,
  params?: { mateId?: number }
): Promise<WriteResponse<MateDispatch>> =>
  post(`/MateDispatchSettle/${id}`, params);

export const ApiGetMateEquipmentPage = (
  request: PageRequest<{ mateId: number }>
): Promise<PageResponse<MateEquipment>> =>
  get("/MateEquipmentPage", {
    pagination: { page: 1, perPage: 999 },
    sort: { field: "dtCreate", order: "desc" },
    ...request,
  });

export const ApiExpandMateEquipmentInventory = (): Promise<
  WriteResponse<MateEquipment>
> => post("/MateEquipment/ExpandInventory");

export const ApiGetMateEquipment = (
  uuid: string
): Promise<WriteResponse<MateEquipment>> => get(`/MateEquipment/${uuid}`);

export const ApiDeleteMateEquipment = (
  id: number
): Promise<WriteResponse<MateEquipment>> => deleteOne(`/MateEquipment/${id}`);

export const ApiPutMateEquipment = (
  id: number,
  request: { isLocked?: boolean }
): Promise<WriteResponse<MateEquipment>> =>
  put(`/MateEquipment/${id}`, request);

export const ApiDecomposeMateEquipment = (
  id: number
): Promise<WriteResponse<MateEquipment>> =>
  post(`/MateEquipmentDecompose/${id}`);

export const ApiDecomposeManyMateEquipment = (
  rarities: EquipmentRarity[]
): Promise<Response<{ totalPowerCubes: number; decomposedCount: number }>> =>
  post(`/MateEquipment/DecomposeMany`, { rarities });

export const ApiOpenMateEquipmentChest = (
  id: number
): Promise<Response<{ powerCubes: number }>> =>
  post(`/MateEquipmentOpen/${id}`);

export const ApiGetMateJournalPage = (
  request: PageRequest<{
    mateId?: number;
    coinRankId?: number;
  }> = {
    pagination: { page: 1, perPage: 999 },
  }
): Promise<PageResponse<MateJournal>> =>
  get("/MateJournalPage", {
    pagination: { page: 1, perPage: 999 },
    sort: { field: "dtCreate", order: "desc" },
    ...request,
  });

export const ApiGetMateJournal = (
  id: number
): Promise<WriteResponse<MateJournal>> => get(`/MateJournal/${id}`);

export const ApiPutMateJournal = (
  id: number,
  request: Partial<MateJournal>
): Promise<WriteResponse<MateJournal>> => put(`/MateJournal/${id}`, request);

export const ApiDeleteMateJournal = (
  id: number
): Promise<WriteResponse<MateJournal>> => deleteOne(`/MateJournal/${id}`);
